import { useState, useEffect, useMemo } from 'react'
import {
  Box, Typography, Checkbox, IconButton, Tooltip,
  TextField, CircularProgress, Skeleton,
  Dialog,
} from '@mui/material'
import CfpSelect from '../../comps/CfpSelect'
import { FancyButton } from '../../comps/FancyButtons'
import EditIcon from '@mui/icons-material/Edit'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import OpenInFullIcon from '@mui/icons-material/OpenInFull'
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined'
import { useApp, useAppProject } from '../../AppContext'
import { addToast } from '../../services/Toasts'
import { pmSaveQuestion } from '../../services/ProjectManagerService'
import { FS_MICRO, FS_XS, FS_SM, ICON_XS, ICON_SM, COLOR_PRIMARY } from '../../common/theme'

interface ExtractedQuestion {
  question: string
  question_number?: string
}

interface ExtractedQuestionsResult {
  questions?: ExtractedQuestion[]
  filesha?: string
  filename?: string
}

interface Props {
  result?: ExtractedQuestionsResult
  streaming?: boolean
}

export function ExtractedQuestionsCard({ result, streaming }: Props) {
  console.log('ExtractedQuestionsCard render')
  const { projects } = useApp()
  const { currentProject } = useAppProject()

  const allQuestions = result?.questions ?? []
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [edits, setEdits] = useState<Record<number, string>>({})
  const [editingIdx, setEditingIdx] = useState<number | null>(null)
  const [dismissed, setDismissed] = useState<Set<number>>(new Set())
  const [chosenSha, setChosenSha] = useState<string | null>(currentProject?.projectsha ?? null)
  const [saving, setSaving] = useState(false)
  const [savedSummary, setSavedSummary] = useState<{ count: number; bidName: string } | null>(null)
  const [expanded, setExpanded] = useState(false)

  // Preselect every row once results land.
  useEffect(() => {
    if (allQuestions.length > 0) {
      setSelected(new Set(allQuestions.map((_, i) => i)))
    }
  }, [allQuestions.length])

  // If currentProject changes (or projects loads after mount), pick a sane default.
  useEffect(() => {
    if (!chosenSha && currentProject?.projectsha) setChosenSha(currentProject.projectsha)
  }, [currentProject?.projectsha])

  const visibleIdxs = useMemo(
    () => allQuestions.map((_, i) => i).filter(i => !dismissed.has(i)),
    [allQuestions.length, dismissed]
  )
  const selectedVisible = visibleIdxs.filter(i => selected.has(i))

  const projectOptions = useMemo(() => {
    const list = projects ?? []
    if (currentProject && !list.some(p => p.projectsha === currentProject.projectsha)) {
      return [currentProject, ...list]
    }
    return list.length > 0 ? list : (currentProject ? [currentProject] : [])
  }, [projects, currentProject])

  const toggle = (i: number) => {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(i)) next.delete(i); else next.add(i)
      return next
    })
  }

  const startEdit = (i: number) => {
    setEdits(prev => ({ ...prev, [i]: prev[i] ?? allQuestions[i].question }))
    setEditingIdx(i)
  }
  const commitEdit = () => setEditingIdx(null)
  const cancelEdit = (i: number) => {
    setEdits(prev => {
      const next = { ...prev }
      delete next[i]
      return next
    })
    setEditingIdx(null)
  }

  const dismiss = (i: number) => {
    setDismissed(prev => new Set(prev).add(i))
    setSelected(prev => {
      const next = new Set(prev)
      next.delete(i)
      return next
    })
  }

  const handleAddToBid = async () => {
    if (!chosenSha || selectedVisible.length === 0) return
    const bid = projectOptions.find(p => p.projectsha === chosenSha)
    setSaving(true)
    let okCount = 0
    let firstErr: string | null = null
    try {
      for (const i of selectedVisible) {
        const text = edits[i] ?? allQuestions[i].question
        const numbering = allQuestions[i].question_number ?? ''
        try {
          const res = await pmSaveQuestion({
            projectsha: chosenSha,
            question: text,
            numbering,
          })
          if (res?.success === false) {
            firstErr = firstErr ?? (res?.msg || 'Save failed')
          } else {
            okCount++
          }
        } catch (e) {
          firstErr = firstErr ?? (e instanceof Error ? e.message : 'Save failed')
        }
      }
      if (okCount > 0) {
        addToast('success', `${okCount} question${okCount === 1 ? '' : 's'} added to ${bid?.name ?? 'bid'}`)
        setSavedSummary({ count: okCount, bidName: bid?.name ?? 'bid' })
        setExpanded(false)
      }
      if (firstErr) addToast('error', firstErr)
    } finally {
      setSaving(false)
    }
  }

  // ── Streaming skeleton ──
  if (streaming && allQuestions.length === 0) {
    return (
      <Box sx={cardSx}>
        <Box sx={headerSx}>
          <HelpOutlineOutlinedIcon sx={{ fontSize: ICON_SM, color: COLOR_PRIMARY }} />
          <Typography variant="caption" sx={{ fontWeight: 600, fontSize: FS_SM }}>
            Forming Extracted Questions{result?.filename ? ` from ${result.filename}` : ''}…
          </Typography>
        </Box>
        {[0, 1, 2].map(i => (
          <Box key={i} sx={{ display: 'flex', alignItems: 'center', px: 1.5, py: 0.5, gap: 1 }}>
            <Skeleton variant="rectangular" width={16} height={16} />
            <Skeleton variant="text" sx={{ flex: 1, fontSize: FS_SM }} />
          </Box>
        ))}
      </Box>
    )
  }

  // ── Saved summary (collapsed) ──
  if (savedSummary) {
    return (
      <Box sx={{ ...cardSx, py: 0.75 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5 }}>
          <CheckIcon sx={{ fontSize: ICON_SM, color: 'success.main' }} />
          <Typography variant="caption" sx={{ fontSize: FS_SM }}>
            {savedSummary.count} question{savedSummary.count === 1 ? '' : 's'} added to <strong>{savedSummary.bidName}</strong>
          </Typography>
        </Box>
      </Box>
    )
  }

  if (allQuestions.length === 0) return null

  // ── Card body (shared between inline and modal) ──
  // `mode` controls layout differences:
  //   - 'inline'  : compact, scrollable rows region capped at 320px
  //   - 'modal'   : fills the dialog, rows region grows with available space
  const renderBody = (mode: 'inline' | 'modal') => {
    const isModal = mode === 'modal'
    return (
      <>
        <Box sx={{ ...headerSx, py: isModal ? 1.25 : 0.75 }}>
          <HelpOutlineOutlinedIcon sx={{ fontSize: ICON_SM, color: COLOR_PRIMARY }} />
          <Typography variant={isModal ? 'subtitle1' : 'caption'} sx={{
            fontWeight: 600,
            fontSize: isModal ? '0.95rem' : FS_SM,
            flex: 1,
          }}>
            {visibleIdxs.length} question{visibleIdxs.length === 1 ? '' : 's'}
            {result?.filename ? ` from ${result.filename}` : ''}
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.55, fontSize: FS_MICRO, mr: 0.5 }}>
            {selectedVisible.length} selected
          </Typography>
          {isModal ? (
            <Tooltip title="Close">
              <IconButton size="small" data-testid="extracted_questions_close_btn"
                onClick={() => setExpanded(false)} sx={{ p: 0.25 }}>
                <CloseIcon sx={{ fontSize: ICON_SM }} />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="Expand">
              <IconButton size="small" data-testid="extracted_questions_expand_btn"
                onClick={() => setExpanded(true)} sx={{ p: 0.25 }}>
                <OpenInFullIcon sx={{ fontSize: ICON_XS }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Rows */}
        <Box sx={{
          ...(isModal
            ? { flex: 1, overflowY: 'auto', minHeight: 0 }
            : { maxHeight: 320, overflowY: 'auto' }),
          borderTop: 1, borderBottom: 1, borderColor: 'divider',
        }}>
          {visibleIdxs.map(i => {
            const q = allQuestions[i]
            const isEditing = editingIdx === i
            const text = edits[i] ?? q.question
            return (
              <Box key={i} sx={{
                display: 'flex', alignItems: 'flex-start', gap: 0.5,
                px: isModal ? 1.5 : 1, py: isModal ? 0.75 : 0.5,
                borderBottom: 1, borderColor: 'divider', '&:last-child': { borderBottom: 0 },
              }}>
                <Checkbox size="small" checked={selected.has(i)} onChange={() => toggle(i)}
                  sx={{ p: 0.25, mt: '2px', flexShrink: 0 }} />
                {q.question_number && (
                  <Typography variant="caption" sx={{
                    fontFamily: 'monospace', fontSize: '0.68rem', opacity: 0.6,
                    pt: '6px', minWidth: 32, flexShrink: 0,
                  }}>
                    {q.question_number}
                  </Typography>
                )}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {isEditing ? (
                    <TextField
                      size="small" fullWidth multiline autoFocus
                      value={text}
                      onChange={e => setEdits(prev => ({ ...prev, [i]: e.target.value }))}
                      onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) commitEdit() }}
                      sx={{ '& .MuiInputBase-input': { fontSize: FS_SM, py: 0.25 } }}
                    />
                  ) : (
                    <Typography variant="caption" sx={{ fontSize: FS_SM, lineHeight: 1.4, display: 'block', py: '4px' }}>
                      {text}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                  {isEditing ? (
                    <>
                      <Tooltip title="Save edit">
                        <IconButton size="small" onClick={commitEdit} sx={{ p: 0.25 }}>
                          <CheckIcon sx={{ fontSize: ICON_XS }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Cancel edit">
                        <IconButton size="small" onClick={() => cancelEdit(i)} sx={{ p: 0.25 }}>
                          <CloseIcon sx={{ fontSize: ICON_XS }} />
                        </IconButton>
                      </Tooltip>
                    </>
                  ) : (
                    <>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => startEdit(i)} sx={{ p: 0.25, opacity: 0.55, '&:hover': { opacity: 1 } }}>
                          <EditIcon sx={{ fontSize: ICON_XS }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Dismiss">
                        <IconButton size="small" onClick={() => dismiss(i)} sx={{ p: 0.25, opacity: 0.55, '&:hover': { opacity: 1 } }}>
                          <CloseIcon sx={{ fontSize: ICON_XS }} />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </Box>
              </Box>
            )
          })}
        </Box>

        {/* Footer: bid selector + add button */}
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1,
          px: isModal ? 1.5 : 1, py: isModal ? 1 : 0.75,
          flexWrap: 'wrap', flexShrink: 0,
        }}>
          <CfpSelect
            label="Bid"
            placeholder={projectOptions.length === 0 ? 'No bids loaded' : 'Select a bid'}
            value={chosenSha ?? ''}
            onChange={(v: string) => setChosenSha(v || null)}
            options={projectOptions.map(p => ({ value: p.projectsha, label: p.name }))}
            disabled={projectOptions.length === 0}
          />
          <Box sx={{ flex: 1 }} />
          <FancyButton onClick={() => setSelected(new Set(visibleIdxs))}>
            Select all
          </FancyButton>
          <FancyButton onClick={() => setSelected(new Set())}>
            Clear
          </FancyButton>
          <FancyButton
            solid
            disabled={saving || !chosenSha || selectedVisible.length === 0}
            onClick={handleAddToBid}
          >
            {saving ? <CircularProgress size={12} sx={{ color: '#fff', mr: 1 }} /> : null}
            Add {selectedVisible.length} to bid
          </FancyButton>
        </Box>
      </>
    )
  }

  return (
    <>
      <Box sx={cardSx} data-testid="extracted_questions_card">
        {renderBody('inline')}
      </Box>
      <Dialog
        open={expanded}
        onClose={() => setExpanded(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          'data-testid': 'extracted_questions_dlg',
          sx: { height: '85vh', display: 'flex', flexDirection: 'column' },
        } as any}
      >
        {renderBody('modal')}
      </Dialog>
    </>
  )
}

const cardSx = {
  border: 1, borderColor: 'divider',
  borderRadius: 2, mt: 0.5, mb: 0.5,
  bgcolor: 'background.paper',
  overflow: 'hidden',
}

const headerSx = {
  display: 'flex', alignItems: 'center', gap: 0.75,
  px: 1.5, py: 0.75,
  bgcolor: 'action.hover',
}
